/**
 * New Education Features Components
 *
 * This file contains the missing features from the README specification:
 * - Career Path Visualiser (6.5)
 * - Life Path Simulator (6.6)
 * - Autism & Special Education Support (6.7)
 * - Child Protection & Safety (6.8)
 * - City & Guilds Exam Page
 * - SAT Exam Page
 */

import React, { useState, useEffect } from 'react';
import {
  GraduationCap, BookOpen, School, Trophy, AlertTriangle, Briefcase,
  DollarSign, HelpCircle, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Search, Filter, MapPin, Phone, Mail, Globe, Calendar, Clock, Users,
  CheckCircle, AlertCircle, Info, Star, Award, Building2, FileText,
  Heart, Shield, Zap, Target, ArrowRight, ExternalLink, X, Cpu, Palette,
  Wrench, User, Home, Lock, Eye, EyeOff, Compass, TrendingUp, BookMarked,
  Brain, Accessibility, PhoneCall, MessageCircle, UserCheck, Scale
} from 'lucide-react';

// Import career paths data
import careerPathsData from '../data/career-paths.json';

// ==================== BACK BUTTON ====================
const BackButton = ({ goBack, label = "Back" }) => (
  <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// ==================== CAREER PATH VISUALISER (6.5) ====================
export const CareerPathVisualiser = ({ setPage, goBack }) => {
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState({
    interests: [],
    strengths: [],
    favouriteSubjects: [],
    academicLevel: '',
    parish: ''
  });
  const [results, setResults] = useState(null);
  const [expandedCareer, setExpandedCareer] = useState(null);

  const interests = [
    { id: 'helping_people', label: 'Helping People', icon: Heart },
    { id: 'technology', label: 'Technology & Computers', icon: Cpu },
    { id: 'business', label: 'Business & Money', icon: Briefcase },
    { id: 'creative', label: 'Creative & Arts', icon: Palette },
    { id: 'science', label: 'Science & Research', icon: BookOpen },
    { id: 'building', label: 'Building & Making', icon: Wrench },
    { id: 'teaching', label: 'Teaching & Training', icon: GraduationCap },
    { id: 'outdoors', label: 'Outdoors & Nature', icon: Globe }
  ];

  const strengths = [
    { id: 'math', label: 'Good with Numbers' },
    { id: 'communication', label: 'Good Communicator' },
    { id: 'problem_solving', label: 'Problem Solver' },
    { id: 'creative', label: 'Creative Thinker' },
    { id: 'hands_on', label: 'Hands-on/Practical' },
    { id: 'detail', label: 'Detail-Oriented' },
    { id: 'leadership', label: 'Natural Leader' },
    { id: 'patience', label: 'Patient & Calm' }
  ];

  const subjects = [
    'Mathematics', 'English', 'Biology', 'Chemistry', 'Physics',
    'Accounts', 'Economics', 'Business', 'Information Technology',
    'Visual Arts', 'Music', 'Technical Drawing', 'Food & Nutrition',
    'History', 'Geography', 'Foreign Languages'
  ];

  const academicLevels = [
    { id: 'primary', label: 'Currently in Primary School' },
    { id: 'lower_secondary', label: 'Form 1-3' },
    { id: 'upper_secondary', label: 'Form 4-5 (doing CSEC)' },
    { id: 'sixth_form', label: 'Sixth Form (doing CAPE)' },
    { id: 'completed_csec', label: 'Completed CSEC' },
    { id: 'completed_cape', label: 'Completed CAPE' },
    { id: 'adult_learner', label: 'Adult / Returning Learner' }
  ];

  const toggleSelection = (category, item) => {
    setSelections(prev => {
      const current = prev[category];
      const updated = current.includes(item)
        ? current.filter(i => i !== item)
        : [...current, item];
      return { ...prev, [category]: updated };
    });
  };

  const calculateResults = () => {
    // Map interests to career categories
    const interestToCategory = {
      helping_people: ['health_medical', 'education'],
      technology: ['engineering_technology'],
      business: ['business_finance'],
      creative: ['creative_arts'],
      science: ['health_medical', 'engineering_technology'],
      building: ['engineering_technology', 'trades_vocational'],
      teaching: ['education'],
      outdoors: ['trades_vocational']
    };

    // Get matching categories
    const matchingCategories = new Set();
    selections.interests.forEach(interest => {
      const categories = interestToCategory[interest] || [];
      categories.forEach(cat => matchingCategories.add(cat));
    });

    // Get careers from matching categories
    const matchedCareers = [];
    careerPathsData.career_categories.forEach(category => {
      if (matchingCategories.has(category.id)) {
        category.careers.forEach(career => {
          // Calculate match score
          let score = 0;

          // Check subject matches
          const subjectMatches = career.required_csec_subjects.filter(
            subj => selections.favouriteSubjects.some(
              fav => subj.toLowerCase().includes(fav.toLowerCase()) ||
                     fav.toLowerCase().includes(subj.toLowerCase())
            )
          );
          score += subjectMatches.length * 20;

          // Interest match
          score += 30;

          matchedCareers.push({
            ...career,
            category: category.name,
            categoryIcon: category.icon,
            matchScore: Math.min(score, 100)
          });
        });
      }
    });

    // Sort by match score
    matchedCareers.sort((a, b) => b.matchScore - a.matchScore);

    setResults(matchedCareers.slice(0, 6));
    setStep(5);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">What interests you most?</h2>
              <p className="text-gray-600 mb-4">Select all that apply (at least 2)</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {interests.map(interest => (
                <button
                  key={interest.id}
                  onClick={() => toggleSelection('interests', interest.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selections.interests.includes(interest.id)
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <interest.icon className={`w-8 h-8 mb-2 ${
                    selections.interests.includes(interest.id) ? 'text-brand-600' : 'text-gray-400'
                  }`} />
                  <div className="font-medium text-sm">{interest.label}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">What are your strengths?</h2>
              <p className="text-gray-600 mb-4">Select your top 3 strengths</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {strengths.map(strength => (
                <button
                  key={strength.id}
                  onClick={() => {
                    if (selections.strengths.includes(strength.id) || selections.strengths.length < 3) {
                      toggleSelection('strengths', strength.id);
                    }
                  }}
                  disabled={!selections.strengths.includes(strength.id) && selections.strengths.length >= 3}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selections.strengths.includes(strength.id)
                      ? 'border-blue-500 bg-blue-50'
                      : selections.strengths.length >= 3
                      ? 'border-gray-100 bg-gray-50 opacity-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CheckCircle className={`w-6 h-6 mb-2 ${
                    selections.strengths.includes(strength.id) ? 'text-blue-600' : 'text-gray-300'
                  }`} />
                  <div className="font-medium text-sm">{strength.label}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">What subjects do you enjoy?</h2>
              <p className="text-gray-600 mb-4">Select your favourite subjects</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {subjects.map(subject => (
                <button
                  key={subject}
                  onClick={() => toggleSelection('favouriteSubjects', subject)}
                  className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                    selections.favouriteSubjects.includes(subject)
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Where are you in your education?</h2>
              <p className="text-gray-600 mb-4">This helps us tailor recommendations</p>
            </div>
            <div className="space-y-2">
              {academicLevels.map(level => (
                <button
                  key={level.id}
                  onClick={() => setSelections(prev => ({ ...prev, academicLevel: level.id }))}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    selections.academicLevel === level.id
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium">{level.label}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-block p-4 bg-brand-100 rounded-full mb-4">
                <Compass className="w-12 h-12 text-brand-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Career Matches</h2>
              <p className="text-gray-600">Based on your interests and strengths</p>
            </div>

            {results && results.length > 0 ? (
              <div className="space-y-4">
                {results.map((career, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedCareer(expandedCareer === index ? null : index)}
                      className="w-full p-5 text-left flex items-center gap-4"
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        index === 0 ? 'bg-amber-100 text-amber-600' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        'bg-brand-100 text-brand-600'
                      }`}>
                        <span className="text-xl font-bold">#{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">{career.career_name}</h3>
                        <p className="text-sm text-gray-500">{career.category}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-brand-600">{career.matchScore}% match</div>
                        <div className="text-xs text-gray-400">{career.demand_level} demand</div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedCareer === index ? 'rotate-180' : ''
                      }`} />
                    </button>

                    {expandedCareer === index && (
                      <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                        <p className="text-gray-600">{career.description}</p>

                        <div className="bg-brand-50 rounded-lg p-4">
                          <div className="font-semibold text-brand-800 mb-1">Salary Range</div>
                          <div className="text-brand-700">{career.average_salary_range}</div>
                        </div>

                        <div>
                          <div className="font-semibold text-gray-700 mb-2">Required CSEC Subjects</div>
                          <div className="flex flex-wrap gap-2">
                            {career.required_csec_subjects.map((subj, i) => (
                              <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                {subj}
                              </span>
                            ))}
                          </div>
                        </div>

                        {career.required_cape_subjects && career.required_cape_subjects.length > 0 && (
                          <div>
                            <div className="font-semibold text-gray-700 mb-2">CAPE Subjects (if continuing)</div>
                            <div className="flex flex-wrap gap-2">
                              {career.required_cape_subjects.map((subj, i) => (
                                <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                  {subj}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <div className="font-semibold text-gray-700 mb-2">Education Pathway</div>
                          <p className="text-gray-600 text-sm">{career.tertiary_pathway}</p>
                        </div>

                        {career.alternative_pathways && (
                          <div>
                            <div className="font-semibold text-gray-700 mb-2">Alternative Routes</div>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {career.alternative_pathways.map((path, i) => (
                                <li key={i}>{path}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div>
                          <div className="font-semibold text-gray-700 mb-2">Skills Needed</div>
                          <div className="flex flex-wrap gap-2">
                            {career.skills_needed.map((skill, i) => (
                              <span key={i} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No strong matches found. Try adjusting your selections.</p>
              </div>
            )}

            <button
              onClick={() => { setStep(1); setResults(null); setSelections({ interests: [], strengths: [], favouriteSubjects: [], academicLevel: '', parish: '' }); }}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
            >
              Start Over
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-8 text-white mb-8">
        <Compass className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Career Path Visualiser</h1>
        <p className="text-indigo-100">Discover careers that match your interests and strengths</p>
      </div>

      {/* Progress indicator */}
      {step < 5 && (
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-500">Step {step} of 4</span>
            <span className="text-sm text-gray-500">{Math.round((step / 4) * 100)}% complete</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {renderStep()}

        {step < 5 && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={() => step === 4 ? calculateResults() : setStep(s => s + 1)}
              disabled={
                (step === 1 && selections.interests.length < 2) ||
                (step === 2 && selections.strengths.length === 0) ||
                (step === 3 && selections.favouriteSubjects.length === 0) ||
                (step === 4 && !selections.academicLevel)
              }
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {step === 4 ? 'See Results' : 'Continue'}
            </button>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Disclaimer:</strong> This tool provides general guidance only. Career paths depend on many factors including personal circumstances, economic conditions, and individual aptitude. Consult with guidance counsellors and professionals for personalised advice.
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== LIFE PATH SIMULATOR (6.6) ====================
export const LifePathSimulator = ({ setPage, goBack }) => {
  const [scenario, setScenario] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [choices, setChoices] = useState([]);
  const [outcome, setOutcome] = useState(null);

  const scenarios = [
    {
      id: 'traditional',
      title: 'Traditional Academic Path',
      description: 'Follow the conventional route: Primary → Secondary → Sixth Form → University',
      icon: GraduationCap,
      startingConditions: { funds: 'moderate', support: 'high', location: 'urban' }
    },
    {
      id: 'vocational',
      title: 'Vocational/TVET Path',
      description: 'Explore technical and vocational training options',
      icon: Wrench,
      startingConditions: { funds: 'low', support: 'moderate', location: 'rural' }
    },
    {
      id: 'challenges',
      title: 'Overcoming Challenges',
      description: 'Navigate education with limited resources or disruptions',
      icon: Shield,
      startingConditions: { funds: 'low', support: 'low', location: 'rural' }
    },
    {
      id: 'returning',
      title: 'Adult Learner Journey',
      description: 'Return to education as an adult',
      icon: Target,
      startingConditions: { funds: 'variable', support: 'self', location: 'any' }
    }
  ];

  const pathSteps = {
    traditional: [
      {
        stage: 'Primary School (Grade 6)',
        description: 'You\'re preparing for PEP exams. Your performance will determine secondary school placement.',
        choices: [
          { id: 'study_hard', label: 'Study intensively for PEP', outcome: { score: 'high', stress: 'high' } },
          { id: 'balanced', label: 'Balance study with activities', outcome: { score: 'moderate', stress: 'low' } },
          { id: 'struggle', label: 'Struggling with preparation', outcome: { score: 'low', stress: 'high' } }
        ]
      },
      {
        stage: 'Secondary School Selection',
        description: 'Based on your PEP results, you have school options.',
        choices: [
          { id: 'traditional_high', label: 'Traditional High School (competitive)', requirement: 'high_score' },
          { id: 'technical_high', label: 'Technical High School', requirement: 'any' },
          { id: 'upgraded_high', label: 'Upgraded High School', requirement: 'any' }
        ]
      },
      {
        stage: 'Form 4 - Subject Selection',
        description: 'Time to choose your CSEC subjects. This affects your future options.',
        choices: [
          { id: 'sciences', label: 'Focus on Sciences (Medicine, Engineering path)', subjects: ['Biology', 'Chemistry', 'Physics', 'Mathematics'] },
          { id: 'business', label: 'Focus on Business (Accounting, Management path)', subjects: ['Accounts', 'Economics', 'POB', 'Mathematics'] },
          { id: 'arts', label: 'Focus on Arts & Humanities', subjects: ['Literature', 'History', 'Visual Arts', 'Languages'] },
          { id: 'technical', label: 'Technical subjects', subjects: ['IT', 'Technical Drawing', 'Industrial Tech'] }
        ]
      },
      {
        stage: 'CSEC Results',
        description: 'Your CSEC results are in. What\'s your situation?',
        choices: [
          { id: 'excellent', label: '8+ passes with Grade I-II', nextOptions: ['sixth_form', 'work', 'tvet'] },
          { id: 'good', label: '5-7 passes with Grade I-III', nextOptions: ['sixth_form', 'work', 'tvet'] },
          { id: 'minimum', label: '5 passes (including Math & English)', nextOptions: ['sixth_form_conditional', 'tvet', 'work'] },
          { id: 'resit', label: 'Need to resit some subjects', nextOptions: ['resit', 'tvet', 'work'] }
        ]
      },
      {
        stage: 'Post-CSEC Decision',
        description: 'What path will you take now?',
        choices: [
          { id: 'sixth_form', label: 'Continue to Sixth Form (CAPE)', path: 'academic' },
          { id: 'tvet', label: 'Vocational Training (HEART/NVQ-J)', path: 'vocational' },
          { id: 'work', label: 'Enter workforce directly', path: 'employment' },
          { id: 'overseas', label: 'Study overseas', path: 'international' }
        ]
      }
    ],
    vocational: [
      {
        stage: 'Starting Point',
        description: 'You\'re interested in hands-on, practical career training. What\'s your current situation?',
        choices: [
          { id: 'after_csec', label: 'Just completed CSEC (any results)' },
          { id: 'during_school', label: 'Still in secondary school' },
          { id: 'left_school', label: 'Left school without completing CSEC' }
        ]
      },
      {
        stage: 'Choosing Your Trade',
        description: 'HEART Trust/NTA offers training in many areas. What interests you most?',
        choices: [
          { id: 'construction', label: 'Construction (Electrical, Plumbing, Masonry)', subjects: ['Hands-on building skills'] },
          { id: 'automotive', label: 'Automotive Technology', subjects: ['Vehicle repair and maintenance'] },
          { id: 'hospitality', label: 'Hospitality & Culinary Arts', subjects: ['Hotel, restaurant, cooking'] },
          { id: 'beauty', label: 'Cosmetology & Beauty', subjects: ['Hair, nails, skincare'] },
          { id: 'technology', label: 'Information Technology', subjects: ['Computer repair, networking'] }
        ]
      },
      {
        stage: 'Training Programme',
        description: 'How will you pursue your training?',
        choices: [
          { id: 'heart_fulltime', label: 'Full-time HEART programme (6-18 months)' },
          { id: 'heart_parttime', label: 'Part-time while working' },
          { id: 'apprenticeship', label: 'On-the-job apprenticeship' },
          { id: 'technical_high', label: 'Technical high school programme' }
        ]
      },
      {
        stage: 'Certification Level',
        description: 'What level of certification are you aiming for?',
        choices: [
          { id: 'nvqj_1', label: 'NVQ-J Level 1 (Entry level)' },
          { id: 'nvqj_2', label: 'NVQ-J Level 2 (Competent worker)' },
          { id: 'nvqj_3', label: 'NVQ-J Level 3 (Supervisor level)' },
          { id: 'city_guilds', label: 'City & Guilds (International certification)' }
        ]
      },
      {
        stage: 'Career Launch',
        description: 'With your certification, what\'s your next move?',
        choices: [
          { id: 'employment', label: 'Seek employment in the field' },
          { id: 'self_employed', label: 'Start your own business' },
          { id: 'further_training', label: 'Pursue higher certification' },
          { id: 'overseas', label: 'Seek opportunities abroad' }
        ]
      }
    ],
    challenges: [
      {
        stage: 'Your Situation',
        description: 'Education can be challenging. What obstacles are you facing?',
        choices: [
          { id: 'financial', label: 'Financial constraints (can\'t afford school fees, books, etc.)' },
          { id: 'family', label: 'Family responsibilities (caring for siblings, working)' },
          { id: 'location', label: 'Geographic challenges (rural area, far from schools)' },
          { id: 'academic', label: 'Academic struggles (learning difficulties, missed school)' }
        ]
      },
      {
        stage: 'Support Options',
        description: 'There are resources that might help. Which applies to you?',
        choices: [
          { id: 'path_eligible', label: 'My family may qualify for PATH support' },
          { id: 'scholarship', label: 'I could apply for scholarships' },
          { id: 'community', label: 'Community organisations might help' },
          { id: 'none_known', label: 'I don\'t know what help is available' }
        ]
      },
      {
        stage: 'Education Route',
        description: 'Given your circumstances, which path seems most realistic?',
        choices: [
          { id: 'continue_school', label: 'Continue regular school with support' },
          { id: 'evening_classes', label: 'Evening classes while working' },
          { id: 'distance_learning', label: 'Distance/online learning' },
          { id: 'tvet_short', label: 'Short-term vocational training' }
        ]
      },
      {
        stage: 'Building Resilience',
        description: 'What strategies will help you succeed?',
        choices: [
          { id: 'mentor', label: 'Find a mentor or guidance counsellor' },
          { id: 'study_group', label: 'Join or form a study group' },
          { id: 'part_time_work', label: 'Balance part-time work with study' },
          { id: 'focus_core', label: 'Focus on core subjects only' }
        ]
      },
      {
        stage: 'Long-term Goal',
        description: 'Despite challenges, what\'s your ultimate aim?',
        choices: [
          { id: 'complete_csec', label: 'Complete at least 5 CSEC subjects' },
          { id: 'get_certified', label: 'Get a vocational certification' },
          { id: 'stable_job', label: 'Find stable employment' },
          { id: 'help_family', label: 'Support my family while studying' }
        ]
      }
    ],
    returning: [
      {
        stage: 'Your Background',
        description: 'Welcome back to education! What\'s your starting point?',
        choices: [
          { id: 'some_csec', label: 'I have some CSEC passes already' },
          { id: 'no_csec', label: 'I never completed CSEC' },
          { id: 'long_gap', label: 'It\'s been many years since I was in school' },
          { id: 'career_change', label: 'I\'m working but want to change careers' }
        ]
      },
      {
        stage: 'Your Goal',
        description: 'What do you want to achieve through further education?',
        choices: [
          { id: 'complete_secondary', label: 'Complete secondary education (CSEC)' },
          { id: 'get_degree', label: 'Eventually get a university degree' },
          { id: 'new_skills', label: 'Learn new job skills' },
          { id: 'promotion', label: 'Qualify for promotion at work' }
        ]
      },
      {
        stage: 'Study Mode',
        description: 'How can you fit education into your life?',
        choices: [
          { id: 'evening', label: 'Evening classes after work' },
          { id: 'weekend', label: 'Weekend programmes' },
          { id: 'online', label: 'Online/distance learning' },
          { id: 'fulltime', label: 'Take time off to study full-time' }
        ]
      },
      {
        stage: 'Institution Choice',
        description: 'Where will you study?',
        choices: [
          { id: 'community_college', label: 'Community college (e.g., Excelsior)' },
          { id: 'heart', label: 'HEART Trust academy' },
          { id: 'utech_evening', label: 'University evening programme' },
          { id: 'private_institution', label: 'Private training institution' }
        ]
      },
      {
        stage: 'Financing',
        description: 'How will you pay for your education?',
        choices: [
          { id: 'self_fund', label: 'Pay from savings/salary' },
          { id: 'slb_loan', label: 'Student loan (SLB)' },
          { id: 'employer', label: 'Employer sponsorship' },
          { id: 'scholarship', label: 'Apply for mature student scholarships' }
        ]
      }
    ]
  };

  const selectScenario = (scenarioId) => {
    setScenario(scenarioId);
    setCurrentStep(0);
    setChoices([]);
    setOutcome(null);
  };

  const makeChoice = (choice) => {
    const newChoices = [...choices, choice];
    setChoices(newChoices);

    const steps = pathSteps[scenario];
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate outcome
      calculateOutcome(newChoices);
    }
  };

  const calculateOutcome = (allChoices) => {
    // Simple outcome calculation based on choices
    const outcomes = {
      academic_success: allChoices.filter(c => ['study_hard', 'excellent', 'sixth_form'].includes(c.id)).length,
      vocational_success: allChoices.filter(c => ['technical_high', 'tvet', 'technical'].includes(c.id)).length,
      balanced: allChoices.filter(c => ['balanced', 'good'].includes(c.id)).length
    };

    let result = {
      title: 'Your Journey Summary',
      description: '',
      nextSteps: [],
      resources: []
    };

    if (outcomes.academic_success >= 3) {
      result.description = 'You\'ve followed a strong academic path. University options are open to you.';
      result.nextSteps = ['Apply to universities', 'Seek scholarships (SLB, JAMVAT)', 'Consider study abroad'];
      result.resources = ['University applications open August-October', 'SLB loan applications'];
    } else if (outcomes.vocational_success >= 2) {
      result.description = 'You\'ve gained practical skills through vocational training. Employment opportunities await.';
      result.nextSteps = ['Complete NVQ-J certification', 'Seek apprenticeships', 'Consider entrepreneurship'];
      result.resources = ['HEART Trust programmes', 'Industry certifications'];
    } else {
      result.description = 'You\'ve taken a balanced approach with multiple options available.';
      result.nextSteps = ['Evaluate all options', 'Seek guidance counselling', 'Consider your strengths'];
      result.resources = ['Career guidance services', 'Alternative pathways'];
    }

    setOutcome(result);
  };

  const resetSimulator = () => {
    setScenario(null);
    setCurrentStep(0);
    setChoices([]);
    setOutcome(null);
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-cyan-700 rounded-2xl p-8 text-white mb-8">
        <TrendingUp className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Life Path Simulator</h1>
        <p className="text-brand-100">Explore different educational journeys and their outcomes</p>
      </div>

      {!scenario ? (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Choose a Scenario</h2>
          {scenarios.map(s => (
            <button
              key={s.id}
              onClick={() => selectScenario(s.id)}
              className="w-full bg-white rounded-xl p-6 text-left shadow-sm hover:shadow-md transition-all border border-gray-100 flex items-center gap-4"
            >
              <div className="p-3 bg-brand-100 rounded-xl">
                <s.icon className="w-8 h-8 text-brand-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.description}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          ))}
        </div>
      ) : outcome ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center mb-6">
            <div className="inline-block p-4 bg-brand-100 rounded-full mb-4">
              <CheckCircle className="w-12 h-12 text-brand-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{outcome.title}</h2>
          </div>

          <p className="text-gray-600 mb-6 text-center">{outcome.description}</p>

          <div className="space-y-4">
            <div className="bg-brand-50 rounded-lg p-4">
              <h3 className="font-semibold text-brand-800 mb-2">Recommended Next Steps</h3>
              <ul className="space-y-2">
                {outcome.nextSteps.map((step, i) => (
                  <li key={i} className="flex items-center gap-2 text-brand-700">
                    <ArrowRight className="w-4 h-4" />
                    {step}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Helpful Resources</h3>
              <ul className="space-y-1">
                {outcome.resources.map((resource, i) => (
                  <li key={i} className="text-blue-700 text-sm">{resource}</li>
                ))}
              </ul>
            </div>
          </div>

          <button
            onClick={resetSimulator}
            className="w-full mt-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
          >
            Try Another Scenario
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {pathSteps[scenario] && pathSteps[scenario][currentStep] && (
            <>
              <div className="mb-6">
                <div className="text-sm text-brand-600 font-medium mb-2">
                  Stage {currentStep + 1} of {pathSteps[scenario].length}
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  {pathSteps[scenario][currentStep].stage}
                </h2>
                <p className="text-gray-600">{pathSteps[scenario][currentStep].description}</p>
              </div>

              <div className="space-y-3">
                {pathSteps[scenario][currentStep].choices.map(choice => (
                  <button
                    key={choice.id}
                    onClick={() => makeChoice(choice)}
                    className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-brand-500 hover:bg-brand-50 text-left transition-all"
                  >
                    <div className="font-medium text-gray-800">{choice.label}</div>
                    {choice.subjects && (
                      <div className="text-sm text-gray-500 mt-1">
                        Subjects: {choice.subjects.join(', ')}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={resetSimulator}
                className="mt-6 text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Start over
              </button>
            </>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Educational Tool Only:</strong> This simulator illustrates possible pathways for educational purposes. Real outcomes depend on many factors. It is not predictive and should not replace professional guidance.
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== AUTISM & SPECIAL EDUCATION SUPPORT (6.7) ====================
export const SpecialEducationSupportPage = ({ setPage, goBack }) => {
  const [activeSection, setActiveSection] = useState(null);

  // MOEY Special Education Unit contacts (December 2024)
  const specialEducationUnit = {
    address: '37 Arnold Road, Kingston 5',
    telephone: ['876-922-1400-9', '876-612-5700-2'],
    extensions: ['2704', '2706'],
    email: 'specialedu@moey.gov.jm',
    services: [
      'Student placement in public and private schools',
      'Shadow support services facilitation',
      'National examinations accommodations facilitation'
    ]
  };

  const regionalContacts = [
    { region: 'Region 1 (Kingston & St. Andrew)', telephone: ['876-922-1400-9'], emails: ['nicolette.thompson@moey.gov.jm', 'specialneedsregion1@moey.gov.jm'] },
    { region: 'Region 2 (St. Thomas, Portland, St. Mary)', telephone: ['876-352-4038', '876-922-1400 ext. 4200'], emails: ['simone.scottbrown@moey.gov.jm'] },
    { region: 'Region 3 (St. Ann, Trelawny)', telephone: ['876-352-4023', '876-917-8440'], emails: ['annika.henryjohnson@moey.gov.jm'] },
    { region: 'Region 4 (St. James, Hanover, Westmoreland)', telephone: ['876-953-6928-30 ext. 4415'], emails: [] },
    { region: 'Region 5 (Manchester, St. Elizabeth)', telephone: ['876-962-1753', '876-962-7098'], emails: ['toween.cwilson@moey.gov.jm'] },
    { region: 'Region 6 (St. Catherine)', telephone: ['876-276-7411'], emails: ['sancia.cheadley@moey.gov.jm'] },
    { region: 'Region 7 (Clarendon)', telephone: ['876-894-0680'], emails: ['bathsheba.shaw@moey.gov.jm'] }
  ];

  const namedSpecialSchools = [
    'Carberry Court Special School',
    'Randolph Lopez School of Hope',
    'Woodlawn School of Special Education',
    'Edgehill School of Special Education',
    'Windsor School of Special Education',
    'Llandilo School of Special Education',
    'St Christopher\'s School for the Deaf',
    'Danny Williams School for the Deaf',
    'Lister Mair Gilby High School for the Deaf'
  ];

  const resources = [
    { name: 'Jamaica Autism Support Association (JASA)', type: 'Support Group', contact: 'jasa.jamaica@gmail.com' },
    { name: 'MOEY Special Education Unit', type: 'Government', contact: specialEducationUnit.email },
    { name: 'Mico University College - Special Education Programme', type: 'Training', contact: 'www.themico.edu.jm' },
    { name: 'Caribbean Child Development Centre (UWI)', type: 'Assessment', contact: '876-927-1884' }
  ];

  const sections = [
    {
      id: 'understanding',
      title: 'Understanding Special Education Needs',
      icon: Brain,
      content: {
        intro: 'Special Education Needs (SEN) encompass a wide range of learning differences and disabilities that may require additional support in educational settings. The MOEY Special Education Unit serves students with the following needs:',
        types: [
          { name: 'Autism', desc: 'Affects social interaction, communication, and may include repetitive behaviours. Early intervention improves outcomes significantly.' },
          { name: 'Learning Disabilities', desc: 'Includes dyslexia, dyscalculia, and other processing differences affecting academic performance.' },
          { name: 'Emotional & Behavioural Disorders', desc: 'Conditions affecting emotional regulation and behaviour in educational settings.' },
          { name: 'Intellectual Disability', desc: 'Below-average intellectual functioning requiring modified curriculum and pacing.' },
          { name: 'Deaf/Hard of Hearing', desc: 'Partial or complete hearing loss requiring sign language, visual aids, or hearing technology.' },
          { name: 'Visual Impairment', desc: 'Partial or complete vision loss requiring Braille, large print, or adapted materials.' },
          { name: 'Physical Impairment', desc: 'May require mobility aids, adapted facilities, or physical therapy.' },
          { name: 'Gifted & Talented', desc: 'Advanced learners who may need enriched curriculum and accelerated opportunities.' }
        ]
      }
    },
    {
      id: 'autism_jamaica',
      title: 'Autism in Jamaica',
      icon: Heart,
      content: {
        intro: 'Autism awareness and support in Jamaica has grown significantly, though challenges remain.',
        points: [
          'Jamaica Autism Support Association (JASA) provides resources and advocacy',
          'Early diagnosis is crucial - signs often appear by age 2-3',
          'There is no "cure" for autism - it is a lifelong neurological difference',
          'Many autistic individuals lead fulfilling, successful lives with appropriate support',
          'Autism presents differently in each person (hence "spectrum")'
        ],
        warning: 'Be wary of unproven "treatments" or claims of cures. Consult registered medical professionals.'
      }
    },
    {
      id: 'school_options',
      title: 'School Placement Options',
      icon: School,
      content: {
        options: [
          {
            type: 'Mainstream with Support',
            desc: 'Regular school with additional accommodations',
            suitable: 'Students who can access standard curriculum with modifications',
            support: 'May include shadow support, resource room, or learning support teacher'
          },
          {
            type: 'Special Education Unit',
            desc: 'Dedicated unit within a mainstream school',
            suitable: 'Students needing more intensive support but benefit from inclusion',
            support: 'Smaller class sizes, specialised teachers, integration opportunities'
          },
          {
            type: 'Special Education School',
            desc: 'Schools designed specifically for students with special needs',
            suitable: 'Students requiring full-time specialised instruction',
            support: 'Highly trained staff, therapeutic services, adapted curriculum'
          }
        ],
        schools_list: namedSpecialSchools,
        note: 'Source: MOEY Special Education Unit brochure (December 2024). Contact the Ministry of Education for a full directory.'
      }
    },
    {
      id: 'shadow_support',
      title: 'Shadow Support Explained',
      icon: UserCheck,
      content: {
        what: 'A shadow (also called one-on-one aide or paraprofessional) is a trained support person who accompanies a student throughout the school day.',
        roles: [
          'Help student stay focused on tasks',
          'Assist with social interactions',
          'Implement behaviour management strategies',
          'Adapt materials and instructions',
          'Support transitions between activities',
          'Communicate with teachers and parents'
        ],
        what_its_not: [
          'A substitute for proper teaching',
          'Someone to do the work for the child',
          'A babysitter or minder',
          'A permanent solution (goal is increasing independence)'
        ],
        finding_shadow: 'Shadow support is typically privately arranged and paid for by parents. Some schools may have their own support staff. Training in ABA (Applied Behaviour Analysis) or special education is beneficial.',
        cost_note: 'Costs vary widely. Discuss expectations and qualifications before hiring.'
      }
    },
    {
      id: 'exam_accommodations',
      title: 'Exam Accommodations',
      icon: FileText,
      content: {
        intro: 'Students with documented special needs may be eligible for exam accommodations from CXC (for CSEC/CAPE) and the Ministry of Education (for PEP).',
        accommodations: [
          { name: 'Extra Time', desc: 'Usually 25-50% additional time' },
          { name: 'Separate Room', desc: 'Quiet space with fewer distractions' },
          { name: 'Reader/Scribe', desc: 'Someone to read questions or write answers' },
          { name: 'Modified Papers', desc: 'Large print, Braille, or coloured paper' },
          { name: 'Breaks', desc: 'Supervised rest periods during exams' },
          { name: 'Use of Technology', desc: 'Computer, calculator, or other aids' }
        ],
        process: [
          '1. Obtain formal diagnosis/assessment from qualified professional',
          '2. Request accommodations through school (early - at least 6 months before exams)',
          '3. School submits application to examination body with supporting documents',
          '4. Accommodations approved/modified by examining body',
          '5. School implements approved accommodations'
        ],
        deadline_note: 'Applications must be submitted well in advance. Late applications may not be processed.'
      }
    },
    {
      id: 'parent_checklist',
      title: 'Parent Action Checklist',
      icon: CheckCircle,
      content: {
        checklist: [
          { item: 'Get a professional assessment', desc: 'Educational psychologist, developmental paediatrician, or relevant specialist' },
          { item: 'Understand your child\'s specific needs', desc: 'Every child is different - learn what works for yours' },
          { item: 'Research school options', desc: 'Visit schools, meet with special education coordinators' },
          { item: 'Know your rights', desc: 'The Education Act provides for inclusive education' },
          { item: 'Build a support team', desc: 'Teachers, therapists, doctors, other parents' },
          { item: 'Document everything', desc: 'Keep records of assessments, IEPs, communications' },
          { item: 'Request IEP meetings', desc: 'Individual Education Plan should be reviewed regularly' },
          { item: 'Advocate consistently', desc: 'Be your child\'s voice while teaching them self-advocacy' },
          { item: 'Connect with support groups', desc: 'JASA, Facebook groups, community organisations' },
          { item: 'Take care of yourself', desc: 'Parent burnout is real - seek support' }
        ]
      }
    }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Accessibility className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Special Education Support</h1>
        <p className="text-white/90">Information and resources for students with special educational needs</p>
      </div>

      {/* Important Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Important:</strong> This page provides general information only. It does not provide medical or diagnostic advice.
            Always consult qualified professionals for assessment and guidance specific to your child's needs.
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4 mb-8">
        {sections.map(section => (
          <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              className="w-full p-5 flex items-center gap-4 text-left"
            >
              <div className="p-3 bg-pink-100 rounded-xl">
                <section.icon className="w-6 h-6 text-pink-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{section.title}</h3>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                activeSection === section.id ? 'rotate-180' : ''
              }`} />
            </button>

            {activeSection === section.id && (
              <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                {section.content.intro && (
                  <p className="text-gray-600 mb-4">{section.content.intro}</p>
                )}

                {section.content.types && (
                  <div className="space-y-3">
                    {section.content.types.map((type, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800">{type.name}</h4>
                        <p className="text-sm text-gray-600">{type.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.content.points && (
                  <ul className="space-y-2">
                    {section.content.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                        {point}
                      </li>
                    ))}
                  </ul>
                )}

                {section.content.warning && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-800">{section.content.warning}</p>
                    </div>
                  </div>
                )}

                {section.content.options && (
                  <div className="space-y-4">
                    {section.content.options.map((option, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800">{option.type}</h4>
                        <p className="text-sm text-gray-600 mb-2">{option.desc}</p>
                        <p className="text-sm"><span className="font-medium">Suitable for:</span> {option.suitable}</p>
                        <p className="text-sm"><span className="font-medium">Support:</span> {option.support}</p>
                      </div>
                    ))}

                    {section.content.schools_list && (
                      <div className="mt-4">
                        <h4 className="font-semibold text-gray-800 mb-2">Special Education Schools in Jamaica</h4>
                        <div className="grid md:grid-cols-2 gap-2">
                          {section.content.schools_list.map((school, i) => (
                            <div key={i} className="text-sm text-gray-600 bg-pink-50 rounded px-3 py-2">
                              {typeof school === 'string' ? school : school.name}
                            </div>
                          ))}
                        </div>
                        {section.content.note && (
                          <p className="text-xs text-gray-500 mt-3 italic">{section.content.note}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {section.content.what && (
                  <div className="space-y-4">
                    <p className="text-gray-600">{section.content.what}</p>

                    <div className="bg-brand-50 rounded-lg p-4">
                      <h4 className="font-semibold text-brand-800 mb-2">What a Shadow Does:</h4>
                      <ul className="space-y-1">
                        {section.content.roles.map((role, i) => (
                          <li key={i} className="text-sm text-brand-700 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> {role}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-800 mb-2">What a Shadow is NOT:</h4>
                      <ul className="space-y-1">
                        {section.content.what_its_not.map((item, i) => (
                          <li key={i} className="text-sm text-red-700 flex items-center gap-2">
                            <X className="w-4 h-4" /> {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {section.content.accommodations && (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                      {section.content.accommodations.map((acc, i) => (
                        <div key={i} className="bg-purple-50 rounded-lg p-3">
                          <h4 className="font-semibold text-purple-800">{acc.name}</h4>
                          <p className="text-sm text-purple-600">{acc.desc}</p>
                        </div>
                      ))}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Application Process:</h4>
                      <ol className="space-y-2">
                        {section.content.process.map((step, i) => (
                          <li key={i} className="text-sm text-gray-600">{step}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                )}

                {section.content.checklist && (
                  <div className="space-y-3">
                    {section.content.checklist.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-pink-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-800">{item.item}</div>
                          <div className="text-sm text-gray-500">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* MOEY Special Education Unit - Official Contacts */}
      <div className="bg-pink-50 rounded-xl border border-pink-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Phone className="w-6 h-6 text-pink-600" />
          MOEY Special Education Unit
        </h2>
        <div className="bg-white rounded-lg p-4 mb-4">
          <p className="text-gray-700 mb-2"><strong>Address:</strong> {specialEducationUnit.address}</p>
          <p className="text-gray-700 mb-2"><strong>Phone:</strong> {specialEducationUnit.telephone.join(' / ')} (Ext. {specialEducationUnit.extensions.join(', ')})</p>
          <p className="text-gray-700 mb-3"><strong>Email:</strong> <span className="text-pink-600">{specialEducationUnit.email}</span></p>
          <div>
            <strong className="text-gray-700">Services Provided:</strong>
            <ul className="mt-2 space-y-1">
              {specialEducationUnit.services.map((service, i) => (
                <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-pink-500 flex-shrink-0 mt-0.5" />
                  {service}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-xs text-gray-500 italic">Source: MOEY Special Education Unit brochure (updated December 2024)</p>
      </div>

      {/* Regional Contacts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Regional Special Education Contacts</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {regionalContacts.map((contact, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 text-sm">{contact.region}</h3>
              <div className="mt-2 space-y-1 text-sm">
                {contact.telephone.map((tel, j) => (
                  <p key={j} className="text-gray-600">
                    <Phone className="w-3 h-3 inline mr-1 text-gray-400" />
                    {tel}
                  </p>
                ))}
                {contact.emails.map((email, j) => (
                  <p key={j} className="text-pink-600 text-xs break-all">
                    <Mail className="w-3 h-3 inline mr-1 text-gray-400" />
                    {email}
                  </p>
                ))}
                {contact.emails.length === 0 && (
                  <p className="text-gray-400 text-xs italic">No email provided</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Resources */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Helpful Resources</h2>
        <div className="space-y-3">
          {resources.map((resource, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-800">{resource.name}</div>
                <div className="text-sm text-gray-500">{resource.type}</div>
              </div>
              <div className="text-sm text-pink-600">{resource.contact}</div>
            </div>
          ))}
        </div>
      </div>

      {/* No Diagnosis Disclaimer */}
      <div className="mt-6 p-4 bg-rose-50 rounded-xl border border-rose-200">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-rose-800">
            <strong>We Do Not Diagnose:</strong> This platform provides information only. We do not diagnose conditions, recommend treatments, or replace professional medical/psychological assessment. If you have concerns about your child's development, please consult a qualified healthcare provider.
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CHILD PROTECTION & SAFETY (6.8) ====================
export const ChildProtectionPage = ({ setPage, goBack }) => {
  const [showExitWarning, setShowExitWarning] = useState(true);

  // Quick exit function
  const quickExit = () => {
    window.location.replace('https://www.google.com/search?q=weather+jamaica');
  };

  return (
    <div className="w-full">
      {/* Quick Exit Warning */}
      {showExitWarning && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white p-3 z-50">
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">If you need to leave this page quickly, click the exit button →</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={quickExit}
                className="px-4 py-1 bg-white text-red-600 rounded font-bold text-sm hover:bg-gray-100"
              >
                Quick Exit
              </button>
              <button onClick={() => setShowExitWarning(false)} className="p-1 hover:bg-red-700 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={showExitWarning ? 'pt-14' : ''}>
        <BackButton goBack={goBack} />

        <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl p-8 text-white mb-8">
          <Shield className="w-12 h-12 mb-4" />
          <h1 className="text-3xl font-black mb-2">Child Protection & Safety</h1>
          <p className="text-slate-300">Information about child safety, reporting abuse, and getting help</p>
        </div>

        {/* Emergency Contacts - Updated January 2026 */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
            <PhoneCall className="w-6 h-6" />
            Emergency Contacts
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border-2 border-red-300">
              <div className="font-bold text-red-700">National Child Abuse Hotline</div>
              <div className="text-3xl font-black text-red-600">211</div>
              <div className="text-sm text-gray-600">24-hour toll-free hotline</div>
              <div className="text-xs text-gray-500 mt-1">Primary reporting number (JIS 2021)</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="font-bold text-red-700">Jamaica Constabulary Force</div>
              <div className="text-2xl font-black text-red-600">119</div>
              <div className="text-sm text-gray-600">Emergency Police</div>
              <div className="text-xs text-gray-500 mt-1">For immediate danger</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="font-bold text-red-700">WhatsApp Reports</div>
              <div className="text-lg font-black text-red-600">876-878-2882</div>
              <div className="text-lg font-black text-red-600">876-822-7031</div>
              <div className="text-xs text-gray-500 mt-1">Text/message reports</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="font-bold text-red-700">Email Reports</div>
              <div className="text-sm font-semibold text-red-600 break-all">report@childprotection.gov.jm</div>
              <div className="text-xs text-gray-500 mt-1">For non-urgent concerns</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="font-bold text-red-700">CISOCA</div>
              <div className="text-2xl font-black text-red-600">876-926-3985</div>
              <div className="text-sm text-gray-600">Centre for Investigation of Sexual Offences</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="font-bold text-red-700">Legacy Number</div>
              <div className="text-lg font-black text-red-600">888-PROTECT</div>
              <div className="text-sm text-gray-600">(1-888-776-8328)</div>
              <div className="text-xs text-gray-500 mt-1">Older number still referenced online</div>
            </div>
          </div>
        </div>

        {/* What is Abuse */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Types of Child Abuse</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-800">Physical Abuse</h3>
              <p className="text-sm text-gray-600">Intentional physical harm including hitting, burning, shaking, or other physical injury. Note: Corporal punishment in schools is illegal in Jamaica.</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-800">Sexual Abuse</h3>
              <p className="text-sm text-gray-600">Any sexual activity with a child, including touching, exposure, exploitation, or grooming. The age of consent in Jamaica is 16.</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-800">Emotional/Psychological Abuse</h3>
              <p className="text-sm text-gray-600">Persistent emotional maltreatment including verbal abuse, constant criticism, threats, rejection, or witnessing domestic violence.</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-800">Neglect</h3>
              <p className="text-sm text-gray-600">Persistent failure to meet a child's basic needs including food, shelter, clothing, medical care, education, or supervision.</p>
            </div>
          </div>
        </div>

        {/* School Responsibilities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <School className="w-6 h-6 text-blue-600" />
            School Responsibilities
          </h2>
          <div className="space-y-3">
            <p className="text-gray-600">Under the Child Care and Protection Act, schools and educators are <strong>mandated reporters</strong>. This means:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Teachers MUST report suspected abuse to the Office of the Children's Registry</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Schools must have child safeguarding policies</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Corporal punishment is PROHIBITED in schools</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">Schools must provide a safe environment for students</span>
              </li>
            </ul>
          </div>
        </div>

        {/* How to Report */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">How to Report</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">1</div>
              <div>
                <h3 className="font-semibold text-blue-800">Call the National Hotline</h3>
                <p className="text-sm text-blue-700"><strong>211</strong> - 24-hour toll-free hotline (primary number)</p>
                <p className="text-xs text-blue-600">Or WhatsApp: 876-878-2882 / 876-822-7031</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">2</div>
              <div>
                <h3 className="font-semibold text-blue-800">Provide Information</h3>
                <p className="text-sm text-blue-700">Child's name, location, details of concern, your contact (can be anonymous)</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">3</div>
              <div>
                <h3 className="font-semibold text-blue-800">Agency Investigates</h3>
                <p className="text-sm text-blue-700">Child Protection and Family Services Agency will assess and respond appropriately</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            You can report anonymously. You are protected by law when making good-faith reports. You do NOT need proof - suspicion is enough to report.
          </p>
        </div>

        {/* For Students */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-purple-800 mb-4 flex items-center gap-2">
            <User className="w-6 h-6" />
            For Students: If You Need Help
          </h2>
          <div className="space-y-3 text-purple-700">
            <p><strong>It is NOT your fault.</strong> No matter what anyone tells you.</p>
            <p><strong>You deserve to be safe.</strong> Everyone does.</p>
            <p><strong>Tell a trusted adult.</strong> A teacher, guidance counsellor, relative, or call the hotline.</p>
            <p><strong>You will be believed.</strong> There are people who want to help you.</p>
          </div>
        </div>

        {/* No Data Capture Notice */}
        <div className="bg-slate-100 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <Lock className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <strong>Privacy Notice:</strong> This page does not collect, store, or transmit any personal data. No information about your visit is recorded. Your privacy is protected.
            </div>
          </div>
        </div>

        {/* Legal Framework */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Scale className="w-6 h-6 text-gray-600" />
            Legal Framework
          </h2>
          <div className="space-y-3 text-sm text-gray-600">
            <p><strong>Child Care and Protection Act (2004)</strong> - Primary legislation protecting children in Jamaica</p>
            <p><strong>Sexual Offences Act</strong> - Addresses sexual crimes against children</p>
            <p><strong>Education Act</strong> - Prohibits corporal punishment in schools</p>
            <p><strong>Domestic Violence Act</strong> - Protects children witnessing or experiencing domestic violence</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CITY & GUILDS EXAM PAGE ====================
export const CityGuildsExamPage = ({ setPage, goBack }) => (
  <div className="max-w-4xl mx-auto">
    <BackButton goBack={goBack} />

    <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
      <div className="inline-block bg-white/20 px-4 py-2 rounded-lg font-black text-2xl mb-4">City & Guilds</div>
      <h1 className="text-3xl font-black mb-2">City & Guilds Certifications</h1>
      <p className="text-white/90">International vocational qualifications recognised worldwide</p>
    </div>

    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What is City & Guilds?</h2>
        <p className="text-gray-600 mb-4">
          City & Guilds is a UK-based organisation offering vocational and technical qualifications that are
          recognised internationally. In Jamaica, City & Guilds certifications are offered through HEART Trust
          and various technical training institutions.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-sm font-medium">International Recognition</span>
          <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-sm font-medium">Vocational Focus</span>
          <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-sm font-medium">Industry-Aligned</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Qualification Levels</h2>
        <div className="space-y-3">
          {[
            { level: 'Level 1', desc: 'Foundation/Entry - Basic skills and knowledge' },
            { level: 'Level 2', desc: 'Intermediate - Competent worker level' },
            { level: 'Level 3', desc: 'Advanced - Skilled/supervisory level' },
            { level: 'Level 4-5', desc: 'Higher - Management/technical specialist' }
          ].map((l, i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-sky-500 text-white rounded-lg flex items-center justify-center font-bold">
                {l.level.split(' ')[1]}
              </div>
              <div>
                <div className="font-semibold text-gray-800">{l.level}</div>
                <div className="text-sm text-gray-500">{l.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Programmes in Jamaica</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Technical</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>Electrical Installation</li>
              <li>Plumbing & Pipe Fitting</li>
              <li>Automotive Technology</li>
              <li>Air Conditioning & Refrigeration</li>
              <li>Welding & Fabrication</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Service Industry</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>Culinary Arts</li>
              <li>Hospitality & Tourism</li>
              <li>Cosmetology</li>
              <li>Business Administration</li>
              <li>Information Technology</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Where to Study</h2>
        <div className="space-y-3">
          <div className="p-4 bg-sky-50 rounded-lg">
            <h3 className="font-semibold text-sky-800">HEART Trust/NTA</h3>
            <p className="text-sm text-sky-700">Primary provider of City & Guilds training in Jamaica through various academies island-wide.</p>
          </div>
          <div className="p-4 bg-sky-50 rounded-lg">
            <h3 className="font-semibold text-sky-800">Technical High Schools</h3>
            <p className="text-sm text-sky-700">Some technical high schools offer City & Guilds alongside NVQ-J and CSEC.</p>
          </div>
          <div className="p-4 bg-sky-50 rounded-lg">
            <h3 className="font-semibold text-sky-800">Private Training Institutions</h3>
            <p className="text-sm text-sky-700">Various approved private institutions offer City & Guilds programmes.</p>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-amber-600" />
          City & Guilds vs NVQ-J
        </h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">City & Guilds</h3>
            <ul className="space-y-1 text-gray-600">
              <li>• International recognition</li>
              <li>• Set by UK standards</li>
              <li>• Widely recognised abroad</li>
              <li>• Theory + practical exams</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">NVQ-J</h3>
            <ul className="space-y-1 text-gray-600">
              <li>• Jamaica-specific</li>
              <li>• Based on local industry needs</li>
              <li>• Growing regional recognition</li>
              <li>• Competency-based assessment</li>
            </ul>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4">Many students pursue both for maximum employability.</p>
      </div>
    </div>
  </div>
);

// ==================== SAT EXAM PAGE ====================
export const SATExamPage = ({ setPage, goBack }) => (
  <div className="max-w-4xl mx-auto">
    <BackButton goBack={goBack} />

    <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
      <div className="inline-block bg-white/20 px-4 py-2 rounded-lg font-black text-2xl mb-4">SAT</div>
      <h1 className="text-3xl font-black mb-2">SAT (Scholastic Assessment Test)</h1>
      <p className="text-white/90">US standardised test for college admissions</p>
    </div>

    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">What is the SAT?</h2>
        <p className="text-gray-600 mb-4">
          The SAT is a standardised test widely used for college admissions in the United States.
          Jamaican students planning to attend US universities typically need to take the SAT.
          The test is administered by the College Board and is offered several times per year in Jamaica.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">US College Admissions</span>
          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Digital Format</span>
          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Multiple Test Dates</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">SAT Structure (Digital SAT)</h2>
        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2">Reading and Writing</h3>
            <p className="text-sm text-red-700">2 modules, 64 minutes total</p>
            <p className="text-sm text-gray-600 mt-1">Tests comprehension, vocabulary, grammar, and rhetoric</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2">Math</h3>
            <p className="text-sm text-red-700">2 modules, 70 minutes total</p>
            <p className="text-sm text-gray-600 mt-1">Covers algebra, geometry, trigonometry, and data analysis. Calculator allowed throughout.</p>
          </div>
        </div>
        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
          <div className="text-sm text-gray-600">
            <strong>Total Time:</strong> Approximately 2 hours 14 minutes<br />
            <strong>Score Range:</strong> 400-1600 (combined)
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Taking the SAT in Jamaica</h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-800">Test Centres</strong>
              <p className="text-sm text-gray-600">Several schools in Jamaica serve as SAT test centres, primarily in Kingston and Montego Bay.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-800">Test Dates</strong>
              <p className="text-sm text-gray-600">Offered multiple times per year (typically March, May, June, August, October, November, December).</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-800">Cost</strong>
              <p className="text-sm text-gray-600">Approximately US$60 + international fee (~US$43). Fee waivers available for eligible students.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Preparation Resources</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-800">Free Resources</h3>
            <ul className="space-y-1 text-sm text-blue-700 mt-2">
              <li>• Khan Academy (Official SAT partner)</li>
              <li>• College Board practice tests</li>
              <li>• Bluebook app (practice digital SAT)</li>
            </ul>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h3 className="font-semibold text-purple-800">Paid Options</h3>
            <ul className="space-y-1 text-sm text-purple-700 mt-2">
              <li>• SAT prep courses (local tutors)</li>
              <li>• Online prep programmes</li>
              <li>• Study guides and books</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Do I Need the SAT?</h2>
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-brand-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-800">Yes, if...</strong>
              <p className="text-sm text-gray-600">Applying to US universities, seeking US-based scholarships, or applying to programmes that require it.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-gray-800">Maybe not, if...</strong>
              <p className="text-sm text-gray-600">Attending Caribbean universities (they use CAPE/CSEC), or applying to test-optional US schools.</p>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          Note: Many US universities have become "test-optional" since 2020. Check specific requirements for your target schools.
        </p>
      </div>

      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-amber-600" />
          SAT vs CAPE for US Universities
        </h2>
        <p className="text-gray-700 text-sm mb-4">
          CAPE results are recognised by many US universities as equivalent to Advanced Placement (AP) courses
          and may earn you college credit. However, the SAT is a separate admissions requirement at many institutions.
        </p>
        <p className="text-gray-600 text-sm">
          Strong CAPE results combined with a good SAT score can make you a competitive applicant for US universities and scholarships.
        </p>
      </div>
    </div>
  </div>
);

// Export all components
export default {
  CareerPathVisualiser,
  LifePathSimulator,
  SpecialEducationSupportPage,
  ChildProtectionPage,
  CityGuildsExamPage,
  SATExamPage
};
